import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { apiFetch, AuthRequiredError } from "../lib/api";
import { trackSignup } from "../lib/analytics";

interface User {
  id: string;
  email: string;
  name: string;
  email_verified?: boolean;
}

interface AuthResponseFull {
  token: string;
  user: User;
}

type AuthResponse = AuthResponseFull | string;

interface AuthContextValue {
  user: User | null;
  token: string | null;
  loading: boolean;
  isSessionExpired: boolean;
  balanceVersion: number;
  refreshBalance: () => void;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, name: string) => Promise<void>;
  googleAuth: (googleToken: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Delay for the second balance refetch after a doorbell trigger, to close
// the backend deduction-vs-completion ordering race (see spec
// 2026-07-02-fix-live-balance-refresh.md § Key Decisions).
const BALANCE_REFRESH_DELAYED_MS = 750;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(
    () => localStorage.getItem("tempo_token"),
  );
  const [loading, setLoading] = useState(true);
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const [balanceVersion, setBalanceVersion] = useState(0);

  // Verify existing token on mount
  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    // Capture whether a token was present before the async call so the catch
    // block can distinguish "expired session" from "genuine guest" (no token).
    const hadTokenAtMount = !!token;

    apiFetch<{ user: User }>("/auth/me")
      .then((me) => {
        console.log("[AuthContext] /auth/me response:", JSON.stringify(me));
        const resolved = me.user ?? me as unknown as User;
        console.log("[AuthContext] resolved user:", JSON.stringify(resolved));
        setUser(resolved);
      })
      .catch((err) => {
        // Only clear token on 401 (invalid/expired token)
        // Keep token on network errors or timeouts
        if (err instanceof AuthRequiredError) {
          localStorage.removeItem("tempo_token");
          setToken(null);
          setUser(null);
          if (hadTokenAtMount) setIsSessionExpired(true);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const saveAuth = useCallback(async (data: AuthResponse) => {
    setIsSessionExpired(false);
    const tokenStr = typeof data === "string" ? data : data.token;
    localStorage.setItem("tempo_token", tokenStr);
    setToken(tokenStr);

    if (typeof data !== "string" && data.user) {
      setUser(data.user);
    } else {
      const me = await apiFetch<{ user: User }>("/auth/me");
      setUser(me.user ?? me as unknown as User);
    }
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      const data = await apiFetch<AuthResponse>("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      saveAuth(data);
    },
    [saveAuth],
  );

  const signup = useCallback(
    async (email: string, password: string, name: string) => {
      // Include guest session ID to migrate any guest transcriptions
      const guestSessionId = localStorage.getItem("tempo_guest_session");
      const data = await apiFetch<AuthResponse>("/auth/signup", {
        method: "POST",
        body: JSON.stringify({ email, password, name, guest_session_id: guestSessionId }),
      });
      // Clear guest session after successful signup (items are now linked to user)
      if (guestSessionId) {
        localStorage.removeItem("tempo_guest_session");
      }
      saveAuth(data);
      trackSignup('email');
    },
    [saveAuth],
  );

  const googleAuth = useCallback(
    async (googleToken: string) => {
      const data = await apiFetch<AuthResponse>("/auth/google", {
        method: "POST",
        body: JSON.stringify({ token: googleToken }),
      });
      saveAuth(data);
      trackSignup('google');
    },
    [saveAuth],
  );

  const logout = useCallback(() => {
    localStorage.removeItem("tempo_token");
    setToken(null);
    setUser(null);
  }, []);

  // Doorbell: bump balanceVersion so consumers with balanceVersion in their
  // useEffect deps refetch the balance endpoint. Fires twice — immediately
  // and after the configured delay — to close the backend deduction race
  // (backend may commit the deduction fractionally after the completion
  // signal reaches the FE).
  const refreshBalance = useCallback(() => {
    setBalanceVersion((v) => v + 1);
    setTimeout(() => {
      setBalanceVersion((v) => v + 1);
    }, BALANCE_REFRESH_DELAYED_MS);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, token, loading, isSessionExpired, balanceVersion, refreshBalance, login, signup, googleAuth, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
